import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, Users, Layers, Send, Link2, Activity as ActivityIcon,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/auth";

const CANONICAL_DOMAINS = [
  { key: "legal", label: "Legal" },
  { key: "housing", label: "Housing" },
  { key: "immigration", label: "Immigration" },
  { key: "retail", label: "Retail" },
  { key: "nourishment", label: "Nourishment" },
  { key: "wellness", label: "Wellness & Wellbeing" },
  { key: "personal_trainers", label: "Personal Trainers" },
  { key: "coaches", label: "Coaches" },
  { key: "therapists", label: "Therapists" },
  { key: "aesthetics", label: "Aesthetics" },
];

const RELATIONSHIP_STATES = [
  "PROSPECT", "APPLIED", "UNDER_REVIEW", "APPROVED", "ACTIVE", "PAUSED", "INACTIVE",
];

const SUB_TYPES = ["resource", "program", "service", "event", "support"];

const REL_STYLE = {
  PROSPECT: "bg-slate-100 text-slate-700",
  APPLIED: "bg-blue-100 text-blue-700",
  UNDER_REVIEW: "bg-amber-100 text-amber-800",
  APPROVED: "bg-emerald-100 text-emerald-700",
  ACTIVE: "bg-emerald-200 text-emerald-900",
  PAUSED: "bg-yellow-100 text-yellow-800",
  INACTIVE: "bg-slate-200 text-slate-600",
};

const SUB_STYLE = {
  DRAFT: "bg-slate-100 text-slate-700",
  SUBMITTED: "bg-blue-100 text-blue-700",
  CHANGES_REQUESTED: "bg-amber-100 text-amber-800",
  RESUBMITTED: "bg-indigo-100 text-indigo-700",
  GOVERNANCE_HANDOFF: "bg-emerald-100 text-emerald-800",
  WITHDRAWN: "bg-slate-200 text-slate-600",
};

const TABS = [
  { key: "overview", label: "Overview", icon: Building2 },
  { key: "relationship", label: "Relationship", icon: Users },
  { key: "domains", label: "Domains", icon: Layers },
  { key: "submissions", label: "Submissions", icon: Send },
  { key: "ecosystem", label: "Ecosystem", icon: Link2 },
  { key: "activity", label: "Activity", icon: ActivityIcon },
];

export default function OrganizationDetailV2() {
  const { orgId } = useParams();
  const [org, setOrg] = useState(null);
  const [rel, setRel] = useState(null);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("overview");
  const [err, setErr] = useState("");
  const [subForm, setSubForm] = useState({ title: "", description: "", submission_type: "resource", domain_keys: [] });
  const [linkForm, setLinkForm] = useState({});

  const load = useCallback(async () => {
    try {
      const [o, r, s] = await Promise.all([
        api.get(`/ops/organizations/${orgId}`),
        api.get(`/ops/organizations/${orgId}/relationship`),
        api.get(`/ops/organizations/${orgId}/submissions`),
      ]);
      setOrg(o.data.organization);
      setRel(r.data);
      setSubs(s.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    setErr("");
    try {
      await api.post(`/ops/organizations/${orgId}/relationship/status`, { status });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const toggleDomain = async (key) => {
    const cur = new Set(rel.domain_associations || []);
    cur.has(key) ? cur.delete(key) : cur.add(key);
    try {
      await api.post(`/ops/organizations/${orgId}/relationship/domains`, {
        domain_keys: Array.from(cur),
      });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const createSubmission = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post(`/ops/organizations/${orgId}/submissions`, subForm);
      setSubForm({ title: "", description: "", submission_type: "resource", domain_keys: [] });
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail));
    }
  };

  const act = async (sid, action, extra = {}) => {
    try {
      await api.post(`/ops/organizations/${orgId}/submissions/${sid}/actions`, { action, ...extra });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  if (!org) return <div className="p-6 text-sm text-slate-500" data-testid="ops-organization-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="ops-organization-detail-v2">
      <Link to="/ops/organizations" className="text-sm text-slate-500" data-testid="ops-organization-back">
        ← Organizations
      </Link>
      <div className="bg-white border rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500 uppercase mb-1">{org.organization_type}</div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="ops-organization-name">{org.organization_name}</h1>
          {org.website && (
            <a href={org.website} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:underline">
              {org.website}
            </a>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            data-testid="ops-organization-relationship-badge"
            className={`px-2 py-0.5 rounded text-xs ${REL_STYLE[rel?.relationship_status] || "bg-slate-100"}`}
          >
            {rel?.relationship_status || "PROSPECT"}
          </span>
          <span className="text-xs text-slate-500">Operational status (not ecosystem visibility)</span>
        </div>
      </div>

      {err && <div className="border border-rose-300 bg-rose-50 text-rose-800 text-sm px-4 py-2 rounded" data-testid="ops-organization-error">{err}</div>}

      <nav className="flex gap-2 flex-wrap" data-testid="ops-organization-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`ops-organization-tab-${t.key}`}
            className={`inline-flex items-center gap-2 text-sm rounded px-3 py-1.5 border ${
              tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "overview" && (
        <section className="bg-white border rounded-xl p-4 space-y-2" data-testid="ops-organization-overview">
          <div className="text-sm"><span className="text-slate-500">Contact:</span> {org.contact_name} · {org.contact_email}</div>
          {org.country && <div className="text-sm"><span className="text-slate-500">Country:</span> {org.country}</div>}
          {org.description && <p className="text-sm text-slate-700 mt-2">{org.description}</p>}
          <div className="text-xs text-slate-500 pt-2">
            Approving this operational relationship does NOT publish this organization to patients.
            Patient visibility remains governed by the Ecosystem B.1–B.6 workflow.
          </div>
        </section>
      )}

      {tab === "relationship" && (
        <section className="bg-white border rounded-xl p-4 space-y-3" data-testid="ops-organization-relationship">
          <h2 className="font-medium text-slate-900">Relationship status</h2>
          <div className="flex gap-2 flex-wrap">
            {RELATIONSHIP_STATES.map((st) => (
              <button
                key={st}
                onClick={() => changeStatus(st)}
                data-testid={`ops-organization-status-btn-${st}`}
                className={`text-xs rounded px-2 py-1 border ${
                  rel?.relationship_status === st ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"
                }`}
              >
                {st}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            Only valid transitions are accepted server-side. Invalid transitions return 409.
          </p>
        </section>
      )}

      {tab === "domains" && (
        <section className="bg-white border rounded-xl p-4 space-y-3" data-testid="ops-organization-domains">
          <h2 className="font-medium text-slate-900">Northstar domain associations</h2>
          <p className="text-xs text-slate-500">Association does not publish content to patients.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {CANONICAL_DOMAINS.map((d) => {
              const on = (rel?.domain_associations || []).includes(d.key);
              return (
                <label key={d.key} className="flex items-center gap-3 border rounded px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    data-testid={`ops-organization-domain-${d.key}`}
                    checked={!!on}
                    onChange={() => toggleDomain(d.key)}
                  />
                  <span>{d.label}</span>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {tab === "submissions" && (
        <section className="bg-white border rounded-xl p-4 space-y-4" data-testid="ops-organization-submissions">
          <h2 className="font-medium text-slate-900">Operational submissions</h2>
          <form onSubmit={createSubmission} className="grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="ops-organization-submission-form">
            <input
              value={subForm.title}
              onChange={(e) => setSubForm({ ...subForm, title: e.target.value })}
              placeholder="Title"
              required
              className="border rounded px-3 py-2 text-sm md:col-span-1"
              data-testid="ops-organization-submission-title"
            />
            <input
              value={subForm.description}
              onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
              placeholder="Short description"
              required
              className="border rounded px-3 py-2 text-sm md:col-span-2"
              data-testid="ops-organization-submission-description"
            />
            <select
              value={subForm.submission_type}
              onChange={(e) => setSubForm({ ...subForm, submission_type: e.target.value })}
              className="border rounded px-3 py-2 text-sm"
              data-testid="ops-organization-submission-type"
            >
              {SUB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button className="md:col-span-4 bg-slate-900 text-white rounded px-3 py-2 text-sm" data-testid="ops-organization-submission-submit">
              Create submission (DRAFT)
            </button>
          </form>
          <div className="space-y-2">
            {subs.length === 0 && <p className="text-sm text-slate-500">No submissions yet.</p>}
            {subs.map((s) => (
              <div key={s.id} className="border rounded p-3" data-testid={`ops-organization-submission-row-${s.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="font-medium text-slate-900">{s.title}</div>
                    <div className="text-xs text-slate-500">{s.submission_type} · {(s.domain_keys || []).join(", ") || "no domains"}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs ${SUB_STYLE[s.status] || "bg-slate-100"}`}>{s.status}</span>
                </div>
                <p className="text-sm text-slate-700 mt-2">{s.description}</p>
                <div className="flex gap-2 mt-2 flex-wrap text-xs">
                  {s.status === "DRAFT" && (
                    <button onClick={() => act(s.id, "submit")} className="bg-blue-100 hover:bg-blue-200 text-blue-800 rounded px-2 py-1" data-testid={`ops-submission-submit-${s.id}`}>Submit</button>
                  )}
                  {s.status === "SUBMITTED" && (
                    <>
                      <button onClick={() => {
                        const reason = window.prompt("Reason for changes?");
                        if (reason) act(s.id, "request_changes", { reason });
                      }} className="bg-amber-100 hover:bg-amber-200 text-amber-800 rounded px-2 py-1" data-testid={`ops-submission-request-changes-${s.id}`}>Request changes</button>
                      <button
                        onClick={() => act(s.id, "handoff")}
                        disabled={!s.linked_ecosystem_entity_id}
                        title={s.linked_ecosystem_entity_id ? "" : "Link an ecosystem entity first"}
                        className="bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded px-2 py-1 disabled:opacity-50"
                        data-testid={`ops-submission-handoff-${s.id}`}
                      >
                        Handoff to B.1–B.6
                      </button>
                    </>
                  )}
                  {s.status === "CHANGES_REQUESTED" && (
                    <button onClick={() => act(s.id, "resubmit")} className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 rounded px-2 py-1" data-testid={`ops-submission-resubmit-${s.id}`}>Resubmit</button>
                  )}
                  {!["GOVERNANCE_HANDOFF", "WITHDRAWN"].includes(s.status) && (
                    <button onClick={() => act(s.id, "withdraw")} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded px-2 py-1" data-testid={`ops-submission-withdraw-${s.id}`}>Withdraw</button>
                  )}
                  {!s.linked_ecosystem_entity_id && (
                    <div className="flex gap-1 items-center">
                      <input
                        placeholder="ecosystem entity id"
                        value={linkForm[s.id] || ""}
                        onChange={(e) => setLinkForm({ ...linkForm, [s.id]: e.target.value })}
                        className="border rounded px-2 py-1 text-xs"
                        data-testid={`ops-submission-link-input-${s.id}`}
                      />
                      <button
                        onClick={() => linkForm[s.id] && act(s.id, "link_entity", { entity_id: linkForm[s.id].trim() })}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded px-2 py-1"
                        data-testid={`ops-submission-link-${s.id}`}
                      >
                        Link entity
                      </button>
                    </div>
                  )}
                  {s.linked_ecosystem_entity_id && (
                    <span className="text-slate-500">Linked: {s.linked_ecosystem_entity_id}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "ecosystem" && (
        <section className="bg-white border rounded-xl p-4 space-y-2" data-testid="ops-organization-ecosystem">
          <h2 className="font-medium text-slate-900">Linked ecosystem entities</h2>
          {(rel?.linked_ecosystem_entity_ids || []).length === 0 && (
            <p className="text-sm text-slate-500">No entities linked. Use Submissions → Link entity to associate a governed ecosystem entity.</p>
          )}
          <ul className="text-sm text-slate-800 space-y-1">
            {(rel?.linked_ecosystem_entity_ids || []).map((eid) => (
              <li key={eid} className="border rounded px-3 py-2">
                <code className="text-xs">{eid}</code>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500 pt-2">
            Linking is a convenience pointer only. Patient visibility, versioning, and grounding remain governed by B.1–B.6.
          </p>
        </section>
      )}

      {tab === "activity" && (
        <section className="bg-white border rounded-xl p-4" data-testid="ops-organization-activity">
          <h2 className="font-medium text-slate-900 mb-2">Activity</h2>
          <p className="text-xs text-slate-500">
            Relationship, domain, and submission events are recorded in the BTC Operations audit log
            (`ecosystem_audit_events`). View filtered by target_kind = ops_organization_profile or
            ops_organization_submission using existing admin tooling.
          </p>
        </section>
      )}
    </div>
  );
}
